'use client';

import { FC, useContext, useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Text,
  Flex,
  Loading,
  RolloverItem,
  Heading,
} from '@/_components';
import { RolloverItemEditorProps } from './RolloverItemEditor.types';
import { deletePriorYearItem } from '@/_api/admin-prior-year-items';
import { PriorYearItem } from '@/_types/models/priorYearItem';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { EditRolloverItem } from './componenets/EditRolloverItem';
export const RolloverItemEditor: FC<RolloverItemEditorProps> = ({
  companyId,
  year,
  isLoading,
  additionalItems,
  rolloverItems,
  loadPriorYearItems,
}) => {
  const [isEditing, setEditing] = useState<boolean>(false);
  const [selectedRolloverItem, setSelectedRolloverItem] = useState<
    PriorYearItem | undefined
  >(undefined);

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  useEffect(() => {
    return () => {
      setEditing(false);
    };
  }, [companyId]);

  const onToggleEdit = () => {
    setSelectedRolloverItem(undefined);
    setEditing(!isEditing);
  };

  const onEditDone = () => {
    setEditing(false);
    loadPriorYearItems();
  };

  const hasRolloverItems: boolean = !!rolloverItems?.length;

  return (
    <Flex isColumn={true} gap={10}>
      <Heading level={6}>Items for {year}</Heading>
      {isLoading ? (
        <Loading size={32} />
      ) : (
        <Flex isColumn={true} gap={6}>
          {hasRolloverItems ? (
            rolloverItems?.map((rolloverItem, key) => {
              const onEditRolloverItem = () => {
                setEditing(true);
                setSelectedRolloverItem(rolloverItem);
              };
              const onDeleteItem = async () => {
                try {
                  await deletePriorYearItem({
                    id: rolloverItem?.id!,
                    companyId: companyId!,
                    token,
                  });

                  addToast('success', 'Rollover Item was succesfully deleted.');
                } catch (error) {
                  addToast('error', null, { error });
                } finally {
                  setSelectedRolloverItem(undefined);
                  loadPriorYearItems();
                }
              };

              if (isEditing && selectedRolloverItem?.id === rolloverItem?.id) {
                return (
                  <EditRolloverItem
                    key={key}
                    rolloverItem={selectedRolloverItem}
                    year={year}
                    companyId={companyId}
                    onDone={onEditDone}
                  />
                );
              }

              return (
                <RolloverItem
                  key={key}
                  item={rolloverItem}
                  onEdit={onEditRolloverItem}
                  onDelete={onDeleteItem}
                  companyId={companyId}
                  isDisabled={isEditing}
                  isDuplicate={additionalItems.includes(rolloverItem.name)}
                />
              );
            })
          ) : (
            <Flex paddingX={4} isBetween={true} paddingY={6}>
              <Text type="small">No Rollover Item</Text>
              <ButtonGroup isSmallGroup={true} isSameLine={true}>
                <Button
                  value="Create New"
                  intent="secondary"
                  onClick={onToggleEdit}
                  size="tiny"
                  iconLeft="plus"
                />
              </ButtonGroup>
            </Flex>
          )}
        </Flex>
      )}
      {isEditing && !selectedRolloverItem?.id && (
        <EditRolloverItem
          rolloverItem={selectedRolloverItem}
          year={year}
          companyId={companyId}
          onDone={onEditDone}
        />
      )}
      {hasRolloverItems && (
        <ButtonGroup isSmallGroup={true} isSameLine={true} align="right">
          <Button
            value="Create New"
            intent="secondary"
            onClick={onToggleEdit}
            size="tiny"
            iconLeft="plus"
          />
        </ButtonGroup>
      )}
    </Flex>
  );
};
